"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AssignedToFieldLogic = void 0;
const isEqual_1 = __importDefault(require("lodash-es/isEqual"));
const texts_1 = require("../../texts");
const types_1 = require("@smartsuite/types");
const user_fieldtype_1 = require("../../safe-field-value/reference-fields/user-field/user.fieldtype");
const dom_1 = require("../../utils/dom");
const base_reference_fieldtype_1 = require("../base-fields/base-reference.fieldtype");
const clipboard_1 = require("../../clipboard");
const comparisons_1 = require("../../comparisons/comparisons");
const fields_1 = require("../../utils/fields");
const filter_1 = require("../../utils/filter");
const reference_1 = require("../../utils/reference");
const uniq_1 = __importDefault(require("lodash-es/uniq"));
const migration_1 = require("../../utils/migration");
const array_1 = require("../../helpers/array");
const record_1 = require("../../utils/record");
const intl_1 = require("../../locale/intl");
class AssignedToFieldLogic extends base_reference_fieldtype_1.BaseReferenceFieldLogic {
    constructor(config, appRecordsService) {
        super(config, appRecordsService);
        this.name = "userfield";
        this.label = (0, intl_1.$t)(texts_1.assignedToFieldText.assigned_to);
        this.icon = "user";
        this.iconColor = "#17C4C4";
        this.placeholder = (0, intl_1.$t)(texts_1.assignedToFieldText.enter_name);
        this.appLookupKey = "slug";
        this.searchSynonyms = [
            (0, intl_1.$t)(texts_1.assignedToFieldText.user),
            (0, intl_1.$t)(texts_1.assignedToFieldText.member),
            (0, intl_1.$t)(texts_1.assignedToFieldText.person),
            (0, intl_1.$t)(texts_1.assignedToFieldText.collaborator),
        ];
        this.groupByMode = "groupBy";
        this.fieldValueGetter = new user_fieldtype_1.UserFieldFieldValueGetter();
        this.clipboardPasteRules = {
            userfield: clipboard_1.clipboardMemberToSelf,
            textfield: clipboard_1.clipboardTextToMember,
            recordtitlefield: clipboard_1.clipboardTextToMember,
            textareafield: clipboard_1.clipboardTextToMember,
            richtextareafield: clipboard_1.clipboardTextToMember,
            lastupdatedfield: clipboard_1.clipboardCreatedUpdatedToMember,
            firstcreatedfield: clipboard_1.clipboardCreatedUpdatedToMember,
            fullnamefield: clipboard_1.clipboardFullNameToMember,
            linkedrecordfield: clipboard_1.clipboardLinkedRecordToMember,
            tagsfield: clipboard_1.clipboardTextToMember,
            singleselectfield: clipboard_1.clipboardTextToMember,
            multipleselectfield: clipboard_1.clipboardTextToMember,
            unknownfield: clipboard_1.clipboardTextToMember,
        };
        // TODO: Make this service mandatory
        const service = appRecordsService;
        this.comparisons.is_exactly = (filterValue) => {
            const currentMember = service.getCurrentMember();
            return (0, comparisons_1.isExactlyUser)(filterValue, currentMember === null || currentMember === void 0 ? void 0 : currentMember.id);
        };
        this.comparisons.has_all_of = (filterValue) => {
            const currentMember = service.getCurrentMember();
            return (0, comparisons_1.hasAllOfUsers)(filterValue, currentMember === null || currentMember === void 0 ? void 0 : currentMember.id);
        };
        this.comparisons.has_any_of = (filterValue) => {
            const currentMember = service.getCurrentMember();
            return (0, comparisons_1.hasAnyOfUsers)(filterValue, currentMember === null || currentMember === void 0 ? void 0 : currentMember.id);
        };
        this.comparisons.has_none_of = (filterValue) => {
            const currentMember = service.getCurrentMember();
            return (0, comparisons_1.hasNoneOfUsers)(filterValue, currentMember === null || currentMember === void 0 ? void 0 : currentMember.id);
        };
        this.patchClipboardRules();
    }
    getInlineEditRules(field) {
        const { params } = field;
        const isMultiple = params.entries_allowed === "multiple";
        const baseInlineEditRules = super.getInlineEditRules(field);
        const switchToInputControl = isMultiple ? "OnCellSelect" : "OnCellClick";
        const cellPresenceEventMode = isMultiple ? "OnModalOpen" : "OnEdit";
        return {
            ...baseInlineEditRules,
            saveKeys: !isMultiple ? [] : types_1.navigationKeys,
            editorWidth: "100%",
            switchToInputControl,
            editOnStartTyping: false,
            enableCopyEventInEditMode: true,
            enablePasteEventInEditMode: true,
            inputControlEditMode: "OnShow",
            cellPresenceEventMode,
            needUseDefaultValue: (value, isDirty) => {
                const isNeedUseDefaultValue = (0, fields_1.needUseDefaultFieldValue)(field, value);
                return isDirty && isNeedUseDefaultValue;
            },
            spaceKeyOpensRecordModal: (isEditMode) => !isEditMode || isMultiple,
        };
    }
    canHaveReferencedRecords() {
        return true;
    }
    getReferencedRecords(fieldValue) {
        if (!Array.isArray(fieldValue)) {
            return [];
        }
        return fieldValue.filter(filter_1.filterUniqueValues).map((id) => ({
            recordId: id,
            application_slug: types_1.MEMBERS_APP_SLUG,
        }));
    }
    processFilterValue(filterValue, comparison) {
        if (["is_empty", "is_not_empty"].includes(comparison)) {
            return filterValue;
        }
        if (["contains", "not_contains"].includes(comparison)) {
            return filterValue;
        }
        return filterValue;
    }
    filterToRecordValue(filterValue, comparison, field) {
        const allowedComparisons = ["is_exactly", "has_all_of"];
        if (!allowedComparisons.includes(comparison) ||
            !(0, reference_1.isReferenceFieldValue)(filterValue)) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        const values = filterValue.map((value) => {
            if (value === types_1.currentMemberChoiceValue) {
                return this.appRecordsService.getCurrentMember().id;
            }
            return value;
        });
        return (0, uniq_1.default)(values);
    }
    isValueEqual(fieldValue1, fieldValue2) {
        return (0, isEqual_1.default)(fieldValue1, fieldValue2);
    }
    getGridColumnSettings(field) {
        const avatarOnlyDisplay = field.params.display_format === "imageOnly";
        return {
            ...super.getGridColumnSettings(field),
            contentAlignment: avatarOnlyDisplay ? "centered" : "left",
        };
    }
    getDefaultValue(field) {
        if (field.params.default_value === types_1.assignedToDefaultValue) {
            const currentMember = this.appRecordsService.getCurrentMember();
            return [currentMember.id];
        }
        return field.params.default_value;
    }
    filterValueToString(filterValue, _field, _userPreferences, _comparison) {
        const currentMemberOptionText = (0, intl_1.$t)(texts_1.assignedToFieldText.current_user);
        if (Array.isArray(filterValue)) {
            const isCurrentMemberOptionSelected = filterValue.includes(types_1.currentMemberChoiceValue);
            const filterStringFromValue = this.valueToString(filterValue);
            if (isCurrentMemberOptionSelected) {
                return filterValue.length > 1
                    ? `${filterStringFromValue}, ${currentMemberOptionText}`
                    : currentMemberOptionText;
            }
            return filterStringFromValue;
        }
        return filterValue;
    }
    getLinkedRecordFieldSmartWidth(field) {
        const widthOptions = {
            image_only: {
                defaultWidht: 90,
                minWidth: 45,
            },
            image_name: {
                defaultWidht: 170,
                minWidth: 90,
            },
        };
        const width = widthOptions[field.params.display_format];
        return {
            defaultWidht: width.defaultWidht,
            minWidth: width.minWidth,
        };
    }
    migrateField(fieldTo, fieldFrom) {
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        return (0, migration_1.migrateDisplayFormat)(migratedField, types_1.assignToDisplayFormats, fieldTo.params.display_format);
    }
    processFieldValue(record, slug, comparison, field) {
        const value = (0, fields_1.getValueFromRecordByField)(record, field);
        if (["contains", "not_contains"].includes(comparison)) {
            return this.valueToString(value);
        }
        return value;
    }
    valueToString(fieldValue) {
        return this.appRecordsService
            .getMultipleRelatedRecords((0, array_1.shallowUnique)(fieldValue))
            .map((item) => (0, record_1.getTitleOrUntitledRecordString)(item))
            .join(", ");
    }
    getContentSize(fieldValue, field, userPreferences, record, font) {
        const { display_format } = field.params;
        const hasName = display_format === "image_name";
        const avatar = hasName ? 30 : 24;
        const records = this.appRecordsService.getMultipleRelatedRecords((0, array_1.shallowUnique)(fieldValue));
        let size = 0;
        if (hasName) {
            const titles = records.map(record_1.getTitleOrUntitledRecordString).join(" ");
            size = (0, dom_1.getTextSize)(titles, undefined, font);
        }
        return size + avatar * records.length;
    }
    getGroupValues(fieldValue) {
        const referenceInstances = this.appRecordsService
            .getMultipleRelatedRecords((0, array_1.shallowUnique)(fieldValue))
            .map((linkedRecord) => [linkedRecord.id]);
        return !referenceInstances.length ? [[]] : referenceInstances;
    }
    patchClipboardRules() {
        const keys = Object.keys(this.clipboardPasteRules);
        keys.forEach((fieldType) => {
            const originalRule = this.clipboardPasteRules[fieldType];
            this.clipboardPasteRules[fieldType] = (clipboardValue, _srcField, targetField, _userPreferences, _currentValue, extra) => {
                return originalRule(clipboardValue, _srcField, targetField, _userPreferences, _currentValue, extra, this.appRecordsService);
            };
        });
    }
}
exports.AssignedToFieldLogic = AssignedToFieldLogic;
